import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import {
  ChevronDown,
  ChevronUp,
  Clock,
  Download,
  Mail,
  MessageCircle,
  Phone,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

const COLLAPSED_ROWS = 4;

function shortDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
}

function csvEscape(v) {
  const s = v === null || v === undefined ? "" : String(v);
  // Wrap in quotes if the value contains comma, quote, or newline; double-up quotes.
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function buildCsv(rows) {
  const header = ["name", "email", "phone", "joined_at", "whatsapp_invite_sent_at"];
  const lines = [header.join(",")];
  for (const u of rows) {
    lines.push([
      csvEscape(u.name || ""),
      csvEscape(u.email || ""),
      csvEscape(u.phone_e164 || ""),
      csvEscape(u.created_at || ""),
      csvEscape(u.whatsapp_invite_sent_at || ""),
    ].join(","));
  }
  return lines.join("\n") + "\n";
}

export default function WhatsAppOptInsCard() {
  const [total, setTotal] = useState(0);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const { data } = await api.get("/admin/whatsapp-opt-ins");
      setTotal(Number(data?.total) || 0);
      setUsers(Array.isArray(data?.users) ? data.users : []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const visibleUsers = useMemo(
    () => (expanded ? users : users.slice(0, COLLAPSED_ROWS)),
    [users, expanded],
  );

  const exportCsv = () => {
    if (!users.length) {
      toast.error("No opt-ins to export.");
      return;
    }
    const csv = buildCsv(users);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `whatsapp-optins-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <section
      data-testid="whatsapp-optins-card"
      className="border border-zinc-200 rounded-sm bg-white p-6"
    >
      <header className="flex items-start justify-between flex-wrap gap-3 mb-5">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 inline-flex items-center gap-1.5">
            <MessageCircle className="h-3.5 w-3.5 text-emerald-600" /> WhatsApp banter group
          </p>
          <h2 className="font-heading text-2xl font-bold tracking-tight text-zinc-950 mt-1">
            Opt-ins.
          </h2>
          <p className="text-sm text-zinc-500 mt-2 max-w-2xl">
            Users who ticked &quot;Add me to the WhatsApp group&quot; at sign-up. Each one is emailed the group invite
            automatically. Use the phone numbers here to add anyone manually if their email landed in spam.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="px-3 py-2 rounded-sm border border-emerald-300 bg-emerald-50 text-center">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-700">Total opt-ins</p>
            <p className="font-heading text-2xl font-bold tabular-nums text-emerald-900" data-testid="whatsapp-optins-total">
              {total}
            </p>
          </div>
          <button
            type="button"
            onClick={exportCsv}
            disabled={loading || users.length === 0}
            data-testid="whatsapp-optins-export"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-zinc-700 hover:text-zinc-950 border border-zinc-200 hover:border-zinc-950 rounded-sm px-3 h-9 disabled:opacity-40"
          >
            <Download className="h-3.5 w-3.5" /> Export CSV
          </button>
          <button
            type="button"
            onClick={load}
            disabled={refreshing}
            data-testid="whatsapp-optins-refresh"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-zinc-600 hover:text-zinc-950"
            aria-label="Refresh opt-in list"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </header>

      {loading ? (
        <p className="text-sm uppercase tracking-[0.18em] text-zinc-500">Loading…</p>
      ) : users.length === 0 ? (
        <div className="text-sm py-10 text-center border border-dashed border-zinc-300 rounded-sm" data-testid="whatsapp-optins-empty">
          <MessageCircle className="h-5 w-5 mx-auto text-zinc-400 mb-2" />
          <p className="text-zinc-700 font-semibold">No opt-ins yet.</p>
          <p className="text-zinc-500 mt-1 max-w-md mx-auto">
            Once registered users tick the WhatsApp opt-in box at sign-up, they&apos;ll appear here sorted alphabetically.
          </p>
        </div>
      ) : (
        <>
          <div
            data-testid="whatsapp-optins-list"
            className="border border-zinc-200 rounded-sm divide-y divide-zinc-100"
          >
            {visibleUsers.map((u) => (
              <div
                key={u.id}
                data-testid={`whatsapp-optin-row-${u.id}`}
                className="px-4 py-3 grid grid-cols-1 md:grid-cols-[1.2fr_1.4fr_1fr_auto] gap-3 items-center hover:bg-zinc-50"
              >
                <div className="min-w-0">
                  <p className="font-semibold text-zinc-950 truncate" data-testid={`whatsapp-optin-name-${u.id}`}>
                    {u.name || "—"}
                  </p>
                  <p className="text-[11px] text-zinc-500 inline-flex items-center gap-1 mt-0.5">
                    <Clock className="h-3 w-3" /> Joined {shortDate(u.created_at)}
                  </p>
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-zinc-700 inline-flex items-center gap-1.5 truncate">
                    <Mail className="h-3.5 w-3.5 text-zinc-400 shrink-0" />
                    <span className="truncate" data-testid={`whatsapp-optin-email-${u.id}`}>{u.email}</span>
                  </p>
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-zinc-700 inline-flex items-center gap-1.5">
                    <Phone className="h-3.5 w-3.5 text-zinc-400 shrink-0" />
                    {u.phone_e164 ? (
                      <a
                        href={`https://wa.me/${u.phone_e164.replace(/[^0-9+]/g, "").replace(/^\+/, "")}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`whatsapp-optin-phone-${u.id}`}
                        className="font-mono tracking-wide text-[#0038FF] hover:underline"
                      >
                        {u.phone_e164}
                      </a>
                    ) : (
                      <span className="text-zinc-400 italic" data-testid={`whatsapp-optin-phone-empty-${u.id}`}>not provided</span>
                    )}
                  </p>
                </div>
                <div className="text-right text-[10px] font-bold uppercase tracking-[0.18em]">
                  {u.whatsapp_invite_sent_at ? (
                    <span className="text-emerald-700" data-testid={`whatsapp-optin-sent-${u.id}`}>
                      Emailed {shortDate(u.whatsapp_invite_sent_at)}
                    </span>
                  ) : (
                    <span className="text-amber-700" data-testid={`whatsapp-optin-not-sent-${u.id}`}>Email pending</span>
                  )}
                </div>
              </div>
            ))}
          </div>
          {users.length > COLLAPSED_ROWS && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              data-testid="whatsapp-optins-toggle"
              className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-zinc-700 hover:text-zinc-950"
            >
              {expanded ? (
                <>
                  <ChevronUp className="h-3.5 w-3.5" />
                  Show fewer (first {COLLAPSED_ROWS})
                </>
              ) : (
                <>
                  <ChevronDown className="h-3.5 w-3.5" />
                  Show all {users.length} opt-ins
                </>
              )}
            </button>
          )}
        </>
      )}
    </section>
  );
}
